import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchApi, fetchApiBlob } from '@/hooks/useApi'
import type {
  FeedbackListParams,
  FeedbackReportDetail,
  FeedbackReportList,
} from '@/types/feedback'

const PAGE_SIZE = 20

export const feedbackKeys = {
  all: ['feedback'] as const,
  list: (params: FeedbackListParams) => ['feedback', 'list', params] as const,
  detail: (id: string) => ['feedback', 'detail', id] as const,
}

function listQuery(params: FeedbackListParams): string {
  const query = new URLSearchParams({
    scope: params.scope,
    page: String(params.page),
    limit: String(PAGE_SIZE),
  })
  if (params.status) query.set('status', params.status)
  if (params.organizationId) query.set('organization_id', params.organizationId)
  return query.toString()
}

export function useFeedbackReports(params: FeedbackListParams) {
  return useQuery({
    queryKey: feedbackKeys.list(params),
    queryFn: () => fetchApi<FeedbackReportList>(`/api/feedback?${listQuery(params)}`),
  })
}

export function useFeedbackReport(id: string | undefined) {
  return useQuery({
    queryKey: feedbackKeys.detail(id ?? ''),
    queryFn: () => fetchApi<FeedbackReportDetail>(`/api/feedback/${id}`),
    enabled: !!id,
  })
}

export function useCreateFeedback() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (form: FormData) =>
      fetchApi<FeedbackReportDetail>('/api/feedback', { method: 'POST', body: form }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: feedbackKeys.all }),
  })
}

/** Answer the team in the report's thread: body and files travel as a form. */
export function useReplyToFeedback(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (form: FormData) =>
      fetchApi<FeedbackReportDetail>(`/api/feedback/${id}/messages`, {
        method: 'POST',
        body: form,
      }),
    onSuccess: (report) => {
      queryClient.setQueryData(feedbackKeys.detail(id), report)
      // The list shows whether the report still awaits a reply.
      queryClient.invalidateQueries({ queryKey: ['feedback', 'list'] })
    },
  })
}

/** Platform admins only: queue again the GitHub issue of a report whose sync failed. */
export function useResyncFeedback(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      fetchApi<FeedbackReportDetail>(`/api/feedback/${id}/resync`, { method: 'POST' }),
    onSuccess: (report) => queryClient.setQueryData(feedbackKeys.detail(id), report),
  })
}

export function attachmentUrl(reportId: string, attachmentId: string): string {
  return `/api/feedback/${reportId}/attachments/${attachmentId}`
}

/**
 * An object URL for an attachment, revoked on unmount.
 *
 * Attachments need the bearer token, so an <img src> pointing at the API
 * would get a 401: the bytes are fetched and handed to the image as a blob.
 */
export function useAttachmentObjectUrl(
  reportId: string,
  attachmentId: string,
  enabled: boolean,
): string | null {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!enabled) return
    let objectUrl: string | null = null
    let cancelled = false
    fetchApiBlob(attachmentUrl(reportId, attachmentId))
      .then((blob) => {
        if (cancelled) return
        objectUrl = URL.createObjectURL(blob)
        setUrl(objectUrl)
      })
      .catch(() => setUrl(null))
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
      // A revoked URL must not outlive its cleanup: disabled, or on its way
      // to another attachment, the hook hands out nothing until the new bytes.
      setUrl(null)
    }
  }, [reportId, attachmentId, enabled])
  return url
}

// Long enough for the new tab to load the blob; revoking it frees the bytes.
const OPENED_URL_LIFETIME_MS = 60_000

/**
 * Open an attachment (a PDF) in a new tab.
 *
 * The tab opens synchronously, inside the click, so pop-up blockers allow it;
 * its location is set once the bytes arrive.
 */
export async function openAttachmentInNewTab(
  reportId: string,
  attachmentId: string,
): Promise<void> {
  const tab = window.open('', '_blank')
  try {
    const blob = await fetchApiBlob(attachmentUrl(reportId, attachmentId))
    const objectUrl = URL.createObjectURL(blob)
    if (tab) tab.location.href = objectUrl
    else window.location.assign(objectUrl)
    setTimeout(() => URL.revokeObjectURL(objectUrl), OPENED_URL_LIFETIME_MS)
  } catch (error) {
    tab?.close()
    throw error
  }
}
